<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { VLoadingSection, SelectCards } from '@/modules/shared/components'

const props = defineProps<{
  individual: any
  onSubmit: (payload: any) => void
}>()

const currentIndividual = computed(() => props.individual)
const skeleton = computed(() => !currentIndividual.value)
const accreditation = computed(() => currentIndividual.value?.accreditation)
const accreditationTemplate = computed(() => accreditation.value?.template)

///////////////////////////////////////////////////////////////////////////////
// Form
///////////////////////////////////////////////////////////////////////////////

const selectedResponseId = ref<string | null>(null)
const error = ref('')

const setForm = () => {
  selectedResponseId.value = accreditation.value?.response_id || null
}

watch(
  currentIndividual,
  (value) => {
    if (value) setForm()
  },
  { immediate: true },
)

watch(selectedResponseId, () => {
  if (error.value) error.value = ''
})

const sectionOptions = (section: any) =>
  section.options.map((option: any) => ({
    value: option.id,
    label: option.option,
    description: '',
  }))

const isValid = async () => {
  if (!selectedResponseId.value) {
    error.value = 'Please select your accreditation status'
    return false
  }
  error.value = ''
  return true
}

const submit = async () => {
  if (!(await isValid())) return
  props.onSubmit({
    response_id: selectedResponseId.value,
  })
}

defineExpose({ isValid, submit })
</script>

<template>
  <div v-if="!skeleton">
    <div v-for="section in accreditationTemplate" :key="section.title">
      <div class="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500">{{ section.title }}</div>

      <div class="mb-6">
        <SelectCards v-model="selectedResponseId" :options="sectionOptions(section)" />
      </div>
    </div>

    <p v-if="error" class="mt-3 text-sm text-red-500">{{ error }}</p>
  </div>
  <VLoadingSection
    v-else
    class="my-40"
    title="Preparing Your Information"
    description="Please wait a moment while we set things up for you."
  />
</template>
